import { getApp, getApps, initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseIsConfigured = Object.values(firebaseConfig).every(Boolean);

function firebaseApp() {
  if (!firebaseIsConfigured) throw new Error("Firebase is not configured for this portal.");
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

function auth() {
  return getAuth(firebaseApp());
}

export async function signInWithGoogle() {
  return signInWithPopup(auth(), new GoogleAuthProvider());
}

export async function signInWithApple() {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return signInWithPopup(auth(), provider);
}

export type PerzaAdminAccess = {
  uid: string;
  displayName: string;
  email: string;
  role: "admin" | "super_admin";
};

export async function readPerzaAdminAccess(user: User): Promise<PerzaAdminAccess | null> {
  const profile = await getDoc(doc(getFirestore(firebaseApp()), "users", user.uid));
  if (!profile.exists()) return null;
  const data = profile.data();
  const role = String(data.role ?? "user");
  if (data.disabled === true || (role !== "admin" && role !== "super_admin")) return null;
  return {
    uid: user.uid,
    displayName: String(data.displayName ?? user.displayName ?? "Perza administrator"),
    email: String(data.email ?? user.email ?? ""),
    role,
  };
}

export function observeSignedInUser(listener: (user: User | null) => void) {
  if (!firebaseIsConfigured) {
    listener(null);
    return () => undefined;
  }
  return onIdTokenChanged(auth(), listener);
}

export async function signOutOfPortal() {
  if (firebaseIsConfigured) await signOut(auth());
}

export async function getCurrentUserToken() {
  const user = auth().currentUser;
  if (!user) throw new Error("UNAUTHENTICATED");
  return user.getIdToken();
}
